import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import {
  useCreateItem,
  useFetchList,
  usePatchItem,
} from 'services/common/react-query-common-service';
import { MASTER_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../components/StylishLoader';

interface NoteItemProps {
  noteId: string;
  name: string;
  time: string;
  avatar: string;
  initials?: string;
  content: string;
  isHighlight?: boolean;
  onMoreClick: (
    event: React.MouseEvent<HTMLElement>,
    noteId: string,
    isHighlight: boolean,
  ) => void;
}

const NoteItem: React.FC<NoteItemProps> = ({
  noteId,
  name,
  time,
  avatar,
  initials,
  content,
  isHighlight,
  onMoreClick,
}) => (
  <Box className={`note-item-card ${isHighlight ? 'highlight' : ''}`}>
    <Box className="note-item-header">
      <Box className="user-info">
        <Box className="avatar-wrapper">
          <Avatar src={avatar} className="user-avatar">
            {initials}
          </Avatar>
          <Box className="status-dot" />
        </Box>
        <Box className="name-time">
          <Typography className="user-name">{name}</Typography>
          <Typography className="note-time">{time}</Typography>
        </Box>
      </Box>
      <IconButton
        size="small"
        className="more-btn"
        onClick={(e) => onMoreClick(e, noteId, !!isHighlight)}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
    </Box>
    <Typography className="note-content">{content}</Typography>
  </Box>
);

interface CandidateNotesProps {
  candidateInfo: any;
  jobId: string;
}

const CandidateNotes: React.FC<CandidateNotesProps> = ({
  candidateInfo,
  jobId,
}) => {
  const [comment, setComment] = useState('');
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNote, setSelectedNote] = useState<{
    id: string;
    isHighlight: boolean;
  } | null>(null);

  const {
    data: notesResp,
    isLoading,
    refetch,
  } = useFetchList<any>(
    ['candidate-notes', candidateInfo?.candidate?.id],
    `${String(
      MASTER_ENDPOINT.Candidates,
    )}/notes?id=&userId=&candidateId=${String(candidateInfo?.candidate?.id)}`,
    {
      enabled: !!candidateInfo?.candidate?.id,
    },
  );

  const { mutateAsync: patchNote } = usePatchItem(true);
  const { mutateAsync: createNote, isPending: isSubmitting } =
    useCreateItem(true);

  const notesData = notesResp?.data || [];

  // Filter highlights based on the API response field
  // The user provided payload uses "highlighted": true/false
  const highlights = notesData.filter((n: any) => n.highlighted);
  const generalNotes = notesData.filter((n: any) => !n.highlighted);

  const handleCommentSubmit = async () => {
    if (!comment.trim()) return;

    const payload = {
      candidateId: candidateInfo?.candidate?.id,
      highlighted: isHighlighted,
      jobId: jobId,
      notes: comment,
      type: 'resumeValidation',
    };

    try {
      await createNote({
        data: payload,
        endpoint: `${String(MASTER_ENDPOINT.Candidates)}/notes`,
      });
      setComment('');
      setIsHighlighted(false);
      refetch();
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  };

  const handleMoreClick = (
    event: React.MouseEvent<HTMLElement>,
    id: string,
    isHighlight: boolean,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedNote({ id, isHighlight });
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedNote(null);
  };

  const handleToggleHighlight = async () => {
    if (!selectedNote) return;
    handleCloseMenu();
    const payload = {
      candidateId: candidateInfo?.candidate?.id,
      highlighted: !selectedNote.isHighlight,
      jobId: jobId,
    };

    try {
      await patchNote({
        data: payload,
        endpoint: `${String(MASTER_ENDPOINT.Candidates)}/notes`,
        id: selectedNote.id,
      });
      refetch();
    } catch (error) {
      console.error('Error toggling highlight:', error);
    }
  };

  const renderNote = (note: any, index: number, isHighlight = false) => {
    const firstName = note?.user?.firstName || '';
    const lastName = note?.user?.lastName || '';
    const initials = (String(firstName[0]) || '') + (String(lastName[0]) || '');
    const name = `${String(firstName)} ${String(lastName)}`;
    const time = moment(note.createdAt).fromNow();
    const avatar = note?.user?.profileImage?.id
      ? `${String(process.env.REACT_APP_APIURL)}${String(
          MASTER_ENDPOINT.Files,
        )}/${String(note.user.profileImage.id)}`
      : '';

    return (
      <NoteItem
        key={note.id || index}
        noteId={note.id}
        name={name}
        time={time}
        avatar={avatar}
        initials={initials}
        content={note.notes}
        isHighlight={isHighlight}
        onMoreClick={handleMoreClick}
      />
    );
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100%"
        py={5}>
        <StylishLoader mode="table" />
      </Box>
    );
  }

  return (
    <Box className="candidate-notes-container inter-font-family">
      <Box className="notes-list-section">
        <Box className="notes-group">
          <Typography className="group-title">Highlights</Typography>
          {highlights.length > 0 ? (
            highlights.map((note: any, index: number) =>
              renderNote(note, index, true),
            )
          ) : (
            <Typography className="no-records">No Records Found</Typography>
          )}
        </Box>

        <Box className="section-divider" />

        <Box className="notes-group">
          <Typography className="group-title">General Notes</Typography>
          {generalNotes.length > 0 ? (
            generalNotes.map((note: any, index: number) =>
              renderNote(note, index),
            )
          ) : (
            <Typography className="no-records">No Records Found</Typography>
          )}
        </Box>
      </Box>

      <Box className="notes-input-section">
        <Box className="input-wrapper">
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add your feedback notes or comments here..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            className="note-textfield"
          />
          <Box className="submit-actions">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isHighlighted}
                  onChange={(e) => setIsHighlighted(e.target.checked)}
                  sx={{
                    '&.Mui-checked': {
                      color: '#242424',
                    },
                    alignSelf: 'flex-start',
                    color: '#242424',
                    padding: '0px !important',
                    paddingRight: '5px !important',
                  }}
                />
              }
              label="Highlight"
              className="highlight-label"
            />
            <Button
              variant="contained"
              className="submit-note-btn"
              startIcon={<SendOutlinedIcon />}
              disabled={isSubmitting || !comment.trim()}
              onClick={handleCommentSubmit}>
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            '& ul': {
              padding: '2px !important',
            },
            borderRadius: '8px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            minWidth: '150px',
          },
        }}>
        <MenuItem
          onClick={handleToggleHighlight}
          sx={{
            color: '#242424',
            fontFamily: "'Inter', sans-serif !important",
            fontSize: '13px',
            py: 1,
          }}>
          {selectedNote?.isHighlight ? 'Remove highlight' : 'Add highlight'}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CandidateNotes;
